import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Menu,
  MenuItem,
  Stack,
  Alert,
  Backdrop,
  CircularProgress,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Apartment as ProjectIcon,
} from "@mui/icons-material";
import {
  readExcelFile,
  exportAsExcel,
  exportAsCSV,
  exportAsPDF,
  downloadExcelFile,
} from "../../../utils/importExport";
import { hasReadPermission, hasWritePermission } from '../../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';
import { showSnackbar } from "../../../components/Snackbar";
import { getProjects, getPlotsByProject, searchFlats, updateFlat, deleteFlat, searchFloorData, createFlat } from "../../../services/project";


const FlatList = () => {

  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const canRead = hasReadPermission('project.apartment-commercial') || hasReadPermission('project');
  const canWrite = hasWritePermission('project.apartment-commercial') || hasWritePermission('project');

  const [draftType, setDraftType] = useState(location.state?.type || "all");
  const [draftProject, setDraftProject] = useState(location.state?.project?.id || location.state?.project || "all");
  const [draftBuilding, setDraftBuilding] = useState(location.state?.building?.id || location.state?.building || "all");
  const [draftFloor, setDraftFloor] = useState(location.state?.floor?.id || location.state?.floor || "all");

  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [activeSelection, setActiveSelection] = useState({
    type: "",
    project: "",
    building: "",
    floor: "",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [floorCatalog, setFloorCatalog] = useState([]);
  const [flatStore, setFlatStore] = useState({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [newFlat, setNewFlat] = useState({
    number: "",
    type: "",
    facing: "",
    price: "",
    plc: "",
    companyDiscountPercent: "",
    costLacs: "",
    guidelineValue: "",
    inventoryType: "",
    status: "",
    remark: "",
  });
  const [editFlatState, setEditFlatState] = useState({
    _id: "",
    number: "",
    type: "",
    facing: "",
    price: "",
    plc: "",
    companyDiscountPercent: "",
    costLacs: "",
    guidelineValue: "",
    inventoryType: "",
    status: "",
    remark: "",
    originalFloorId: "",
  });
  const [addDialogType, setAddDialogType] = useState("");
  const [addDialogProject, setAddDialogProject] = useState("");
  const [addDialogBuildingId, setAddDialogBuildingId] = useState("");
  const [addDialogBuildings, setAddDialogBuildings] = useState([]);
  const [addDialogFloorId, setAddDialogFloorId] = useState("");
  const [editDialogType, setEditDialogType] = useState("");
  const [editDialogProject, setEditDialogProject] = useState("");
  const [editDialogBuildingId, setEditDialogBuildingId] = useState("");
  const [editDialogFloorId, setEditDialogFloorId] = useState("");
  const fileInputRef = useRef(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const [projects, setProjects] = useState([]);
  const [apiBuildings, setApiBuildings] = useState([]);
  const [flatTypeOptions, setFlatTypeOptions] = useState([]);
  const [facingOptions, setFacingOptions] = useState([]);
  const lastProjectTypeLoaded = useRef(null);
  const flatTypesLoadedRef = useRef(false);
  const facingLoadedRef = useRef(false);

  const extractInventories = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.inventories)) return payload.inventories;
    if (Array.isArray(payload?.data?.inventories)) return payload.data.inventories;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const loadFlatTypes = useCallback(async () => {
    if (flatTypesLoadedRef.current) return;
    flatTypesLoadedRef.current = true;
    try {
      const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=flatType`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );
      const body = await response.json().catch(() => ({}));
      const list = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body?.details)
          ? body.details
          : Array.isArray(body)
            ? body
            : [];

      const options = list
        .map((item) => {
          const label =
            item?.details?.flatType ||
            item?.details?.flatTypeName ||
            item?.details?.plcName ||
            item?.plcName ||
            item?.name ||
            item?.value ||
            "";
          if (!label) return null;
          // Use label as both id and label so we display and send the same value
          return { id: label, label };
        })
        .filter(Boolean);

      setFlatTypeOptions(options);
    } catch (error) {
      console.error("[FlatList] Failed to load flat types", error);
      setFlatTypeOptions([]);
    }
  }, []);

  const loadFacingOptions = useCallback(async () => {
    if (facingLoadedRef.current) return;
    facingLoadedRef.current = true;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/globalMasterDetails?type=facing`
      );
      const body = await response.json().catch(() => ({}));
      const list = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body?.details)
          ? body.details
          : Array.isArray(body)
            ? body
            : [];
      const options = list
        .map((item) => {
          const id = item._id || item.id;
          const label =
            item?.details?.facingName ||
            item?.facingName ||
            item?.name ||
            item?.value ||
            "";
          if (!id || !label) return null;
          return { id, label };
        })
        .filter(Boolean);
      setFacingOptions(options);
    } catch (error) {
      console.error("[FlatList] Failed to load facing options", error);
      setFacingOptions([]);
    }
  }, []);

  const getFlatTypeLabel = useCallback(
    (value) => {
      const match = flatTypeOptions.find(
        (opt) => String(opt.id) === String(value) || opt.label === value
      );
      return match?.label || value;
    },
    [flatTypeOptions]
  );

  const getFlatTypeValue = useCallback(
    (value) => {
      const match = flatTypeOptions.find(
        (opt) => String(opt.id) === String(value) || opt.label === value
      );
      return match?.id || value;
    },
    [flatTypeOptions]
  );

  const getFacingLabel = useCallback(
    (value) => {
      const match = facingOptions.find(
        (opt) => String(opt.id) === String(value) || opt.label === value
      );
      return match?.label || value;
    },
    [facingOptions]
  );

  const getFacingValue = useCallback(
    (value) => {
      const match = facingOptions.find(
        (opt) => String(opt.id) === String(value) || opt.label === value
      );
      return match?.id || value;
    },
    [facingOptions]
  );

  const loadProjectsByType = useCallback(
    async (type) => {
      if (!type || type === "all") {
        lastProjectTypeLoaded.current = null;
        setProjects([]);
        return;
      }
      const normalized = type;
      if (lastProjectTypeLoaded.current === normalized) return;
      lastProjectTypeLoaded.current = normalized;
      try {
        const data = await getProjects({ projectType: normalized });
        const list = Array.isArray(data) ? data : [];
        setProjects(list);
        if (list.length === 1 && (draftProject === "all" || !draftProject)) {
          setDraftProject(list[0]._id || list[0].id);
        }
      } catch {
        setProjects([]);
      }
    },
    []
  );

  useEffect(() => {
    loadProjectsByType(draftType);
  }, [draftType, loadProjectsByType]);

  const selectedProjectId = useMemo(() => {
    if (!draftProject || draftProject === 'all') return '';
    return String(draftProject);
  }, [draftProject]);
  const canAddFlat = Boolean(selectedProjectId);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        // Only load buildings when a project is selected
        if (!selectedProjectId) {
          if (active) setApiBuildings([]);
          return;
        }

        const inventoryType = draftType === "all" ? undefined : draftType;
        const projectType = draftType === "all" ? undefined : draftType;

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[FlatList] fetching buildings', {
            projectId: selectedProjectId,
            inventoryType,
            projectType,
          });
        }

        const response = await getPlotsByProject(
          selectedProjectId,
          inventoryType,
          projectType,
        );
        const inventories = extractInventories(response);
        const buildings = inventories
          .filter((building) => {
            const bType = (building.inventoryType || "Apartment").toLowerCase();
            const targetType = (inventoryType || "Apartment").toLowerCase();
            return bType === targetType || (targetType === 'commercial' && bType === 'apartment') || (targetType === 'apartment' && bType === 'commercial');
          })
          .map((b) => ({
            id: b._id || b.id || '',
            name: b.buildingName || b.name || 'Unnamed Building',
            projectId: b.projectId || b.projectData?._id || b.project_id || '',
            projectName: b.projectName || b.projectData?.projectName || b.project || '',
            type: b.projectType || b.projectData?.projectType || b.inventoryType || 'Apartment',
          }));
        if (active) {
          setApiBuildings(buildings);
          if (buildings.length === 1 && (draftBuilding === "all" || !draftBuilding)) {
            setDraftBuilding(buildings[0].id);
          }
        }
      } catch (e) {
        if (active) setApiBuildings([]);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [selectedProjectId, draftType]);

  useEffect(() => {
    if (!projects.length) return;
    if (!draftProject || draftProject === 'all') return;
    const existsById = projects.some((p) => String(p._id || p.id) === String(draftProject));
    if (existsById) return;
    const match = projects.find((p) => (p.projectName || p.name) === draftProject);
    if (match) {
      setDraftProject(match._id || match.id);
    }
  }, [projects]);

  useEffect(() => {
    if (!addDialogType) return;
    loadProjectsByType(addDialogType);
  }, [addDialogType, loadProjectsByType]);

  useEffect(() => {
    if (!editDialogType) return;
    loadProjectsByType(editDialogType);
  }, [editDialogType, loadProjectsByType]);

  useEffect(() => {
    if (draftProject === 'all') {
      setFloorCatalog([]);
      return;
    }
    
    let active = true;
    const loadFloors = async () => {
      try {
        const buildingIdArg = draftBuilding === 'all' ? undefined : draftBuilding;
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[FlatList] fetching floors', { projectId: draftProject, buildingId: buildingIdArg });
        }
        const data = await searchFloorData({ projectId: draftProject, buildingId: buildingIdArg });
        const rawFloors = extractInventories(data);
        const normalizedFloors = rawFloors.map((f, idx) => {
          const building = apiBuildings.find((b) => b.id === (f.buildingId || f.building_id) || b._id === (f.buildingId || f.building_id));
          return {
            _id: f._id || f.id || '',
            id: f._id || f.id || '',
            buildingId: f.buildingId || f.building_id || "",
            buildingName: building?.name || f.buildingName || "",
            floorNo: Number.isFinite(Number(f.floorNo)) ? Number(f.floorNo) : idx + 1,
            name: f.name || f.floorName || f.title || (Number.isFinite(Number(f.floorNo)) ? `Floor ${f.floorNo}` : (f.floorNo || `Floor ${idx + 1}`)),
            totalFlats: Number.isFinite(Number(f.totalFlats || f.totalFlat))
              ? Number(f.totalFlats || f.totalFlat)
              : (f.totalFlats || f.totalFlat) || 0,
            status: f.status || "Planned",
            remark: f.remark || f.notes || "",
          };
        });
        if (active) setFloorCatalog(normalizedFloors);
      } catch (e) {
        if (active) setFloorCatalog([]);
      }
    };
    loadFloors();
    return () => {
      active = false;
    };
  }, [draftProject, draftBuilding]);

  useEffect(() => {
    if (location.state && draftProject !== 'all' && draftBuilding !== 'all' && floorCatalog.length > 0) {
      if (!hasSearched) {
        handleSearch(false);
      }
    }
  }, [location.state, draftProject, draftBuilding, floorCatalog.length, hasSearched]);

  // Load floors when selecting building inside Add dialog (so dropdown is populated)
  useEffect(() => {
    if (!addDialogBuildingId) return;
    let active = true;
    const loadDialogFloors = async () => {
      try {
        const projectIdArg =
          addDialogProject ||
          (draftProject && draftProject !== "all" ? draftProject : undefined);
        const floors = await searchFloorData({
          projectId: projectIdArg,
          buildingId: addDialogBuildingId,
        });
        if (!active) return;
        const normalizedFloors = (floors || []).map((f, idx) => ({
          id: f._id || f.id || f.floorId || `FLR-${idx + 1}`,
          buildingId: f.buildingId || addDialogBuildingId,
          floorNo: Number.isFinite(Number(f.floorNo)) ? Number(f.floorNo) : idx + 1,
          name: f.name || f.floorName || f.title || `Floor ${idx + 1}`,
          totalFlats: Number.isFinite(Number(f.totalFlats || f.totalFlat))
            ? Number(f.totalFlats || f.totalFlat)
            : (f.totalFlats || f.totalFlat) || 0,
          status: f.status || "Planned",
          remark: f.remark || f.notes || "",
        }));

        setFloorCatalog((prev) => {
          const others = (prev || []).filter(
            (item) => (item.buildingId || item.id) !== addDialogBuildingId
          );
          return [...others, ...normalizedFloors];
        });
      } catch (e) {
        if (active) {
          console.error("[FlatList] Error loading floors for add dialog", e);
        }
      }
    };
    loadDialogFloors();
    return () => {
      active = false;
    };
  }, [addDialogBuildingId, addDialogProject, draftProject]);

  const resetImportState = () => {
    setImportFileName("");
    setSelectedImportFile(null);
    setImportError("");
    setImportSuccess("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImportClick = () => {
    resetImportState();
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    resetImportState();
    setImportDialogOpen(false);
  };

  const handleChooseImportFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setSelectedImportFile(file);
      setImportFileName(file.name);
    }
  };

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const getFloorDetails = (floorId) => floorCatalog.find((floor) => (floor._id || floor.id) === floorId) || null;

  const handlePerformImport = async () => {
    if (!selectedImportFile) return;
    try {
      const rows = await readExcelFile(selectedImportFile);
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Excel sheet is empty or invalid.");
      }
      const timestamp = Date.now();
      const fallbackFloorId =
        activeSelection.floor ||
        (draftFloor !== "all" ? draftFloor : "");

      const floorsToAdd = [];
      const groupedFlats = rows.reduce((acc, row, index) => {
        const floorId =
          row.floorNo ||
          row.FloorNo ||
          row.floorId ||
          row.FloorId ||
          row.floor ||
          row.Floor ||
          fallbackFloorId;

        const flatNumber =
          row.flatNumber ||
          row.FlatNumber ||
          row.number ||
          row.Number ||
          "";

        if (!floorId || !flatNumber) {
          return acc;
        }

        const normalizedFloorId = String(floorId).trim();
        const existingFloor = getFloorDetails(normalizedFloorId);
        if (!existingFloor) {
          const buildingId =
            row.buildingId || row.BuildingId || row["Building Id"] || row["BuildingID"] || "";
          const floorName =
            row.floorName || row.FloorName || row.name || row.Name || `Imported Floor ${index + 1}`;
          if (buildingId) {
            floorsToAdd.push({
              id: normalizedFloorId,
              name: floorName,
              buildingId,
            });
          }
        }

        const flat = {
          id: row.id || row.ID || `IMP-FLT-${timestamp}-${index + 1}`,
          number: flatNumber,
          type: row.type || row.Type || row.configuration || row.Configuration || "",
          facing: row.facing || row.Facing || "",
          price: row.price || row.Price || "",
          plc: row.plc || row.Plc || row["PLC %"] || "",
          costLacs: row.costLacs || row.CostLacs || row["Cost (Lacs)"] || "",
          guidelineValue: row.guidelineValue || row.GuidelineValue || row["Circle Rate"] || "",
          inventoryType: row.inventoryType || row.InventoryType || "",
          status: row.status || row.Status || "select status",
          remark: row.remark || row.Remark || "",
        };

        if (!acc[normalizedFloorId]) {
          acc[normalizedFloorId] = [];
        }
        acc[normalizedFloorId].push(flat);
        return acc;
      }, {});

      const importedFloorIds = Object.keys(groupedFlats);
      if (!importedFloorIds.length) {
        throw new Error("No valid flat records were found in the file.");
      }

      if (floorsToAdd.length) {
        setFloorCatalog((prev) => {
          const existingIds = new Set(prev.map((floor) => floor.id));
          const additions = floorsToAdd.filter((floor) => !existingIds.has(floor.id));
          return additions.length ? [...prev, ...additions] : prev;
        });
      }

      setFlatStore((prev) => {
        const next = { ...prev };
        importedFloorIds.forEach((floorId) => {
          next[floorId] = groupedFlats[floorId];
        });
        return next;
      });

      setImportSuccess(`Imported flats for ${importedFloorIds.length} floor(s) successfully.`);
      setImportError("");
      setHasSearched(false);
      setActiveSelection({ type: "", project: "", building: "", floor: "" });
      setDraftType("all");
      setDraftProject("all");
      setDraftBuilding("all");
      setDraftFloor("all");
    } catch (error) {
      setImportError(error.message || "Failed to import flats. Please check the file format.");
      setImportSuccess("");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDownloadSample = () => {
    try {
      downloadExcelFile({
        rows: [
          {
            floorNo: "G-001",
            flatNumber: "A-101",
            type: "3 BHK",
            facing: "East",
            price: "6200000",
            plc: "4%",
            costLacs: "62",
            guidelineValue: "5800000",
            inventoryType: "Residential",
            status: "Available",
            remark: "Sample flat row",
          },
        ],
        sheetName: "Sample",
        fileName: "flat_import_sample.xlsx",
      });
    } catch (error) {
      setImportError(error.message || "Failed to download sample file.");
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadExcelFile({
        rows: [
          {
            floorNo: "",
            flatNumber: "",
            type: "",
            facing: "",
            price: "",
            plc: "",
            costLacs: "",
            guidelineValue: "",
            inventoryType: "",
            status: "",
            remark: "",
          },
        ],
        sheetName: "Template",
        fileName: "flat_import_template.xlsx",
      });
    } catch (error) {
      setImportError(error.message || "Failed to download template file.");
    }
  };


  useEffect(() => {
    const incomingBuilding = location.state?.building;
    const incomingFloor = location.state?.floor;
    if (incomingBuilding && incomingFloor) {
      const normalizedType = incomingBuilding.type || incomingBuilding.projectType || "Apartment";
      const project = incomingBuilding.project || incomingBuilding.projectId || "all";
      setDraftType(normalizedType);
      setDraftProject(project);
      setDraftBuilding(incomingBuilding.id || "all");
      setDraftFloor(incomingFloor.id || "all");
      setActiveSelection({
        type: normalizedType,
        project: project,
        building: incomingBuilding.id || "all",
        floor: incomingFloor.id || "all",
      });
      setHasSearched(true);
      setPage(0);
    }
  }, [location.state]);

  const projectOptions = useMemo(() => {
    const opts = (projects || []).map((p) => ({
      value: p._id || p.id,
      label: p.projectName || p.name || 'Unnamed',
    }));
    return [{ value: 'all', label: 'Select Project' }, ...opts];
  }, [projects]);

  const buildingOptions = useMemo(() => {
    return apiBuildings;
  }, [apiBuildings]);

  const floorOptions = useMemo(() => {
    if (draftProject === "all") {
      return [];
    }

    return floorCatalog;
  }, [draftBuilding, floorCatalog]);

  const propertyTypeOptions = useMemo(() => {
    return ["Apartment", "Commercial"];
  }, []);

  const projectsForAddDialog = useMemo(() => {
    if (!addDialogType) return [];
    return (projects || [])
      .filter(
        (p) =>
          (p.type || '').toLowerCase() === addDialogType.toLowerCase(),
      )
      .map((p) => ({
        id: p._id || p.id,
        name: p.projectName || p.name || 'Unnamed',
      }));
  }, [addDialogType, projects]);

  const buildingsForAddDialog = useMemo(() => addDialogBuildings, [addDialogBuildings]);

  const floorsForAddDialog = useMemo(() => {
    if (!addDialogBuildingId) return [];
    return floorCatalog.filter((floor) => floor.buildingId === addDialogBuildingId);
  }, [addDialogBuildingId, floorCatalog]);

  // Load buildings for Add Flat modal based on selected type + project
  useEffect(() => {
    let active = true;
    const loadAddBuildings = async () => {
      try {
        if (!addDialogType || !addDialogProject) {
          if (active) setAddDialogBuildings([]);
          return;
        }
        const inventoryType = addDialogType;
        const projectType = addDialogType;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[FlatList] (Add Dialog) fetching buildings', {
            projectId: addDialogProject,
            inventoryType,
            projectType,
          });
        }
        const response = await getPlotsByProject(
          addDialogProject,
          inventoryType,
          projectType,
        );
        const inventories = extractInventories(response);
        const buildings = inventories.map((b) => ({
          id: b._id || b.id || '',
          name: b.buildingName || b.name || 'Unnamed Building',
          projectId: b.projectId || b.projectData?._id || b.project_id || '',
          projectName: b.projectName || b.projectData?.projectName || b.project || '',
          type: b.projectType || b.projectData?.projectType || b.inventoryType || '',
        }));
        if (active) setAddDialogBuildings(buildings);
      } catch (e) {
        if (active) setAddDialogBuildings([]);
      }
    };
    loadAddBuildings();
    return () => {
      active = false;
    };
  }, [addDialogType, addDialogProject]);

  const projectsForEditDialog = useMemo(() => {
    if (!editDialogType) return [];

    const filtered = apiBuildings.filter(
      (building) => building.type === editDialogType || !building.type
    );

    const byId = new Map();
    filtered.forEach((building) => {
      const id = building.projectId || building.project_id || "";
      const name = building.projectName || building.project || "Unnamed";
      if (!id) return;
      const normalizedId = String(id);
      if (byId.has(normalizedId)) return;
      byId.set(normalizedId, { id: normalizedId, name });
    });

    return Array.from(byId.values());
  }, [editDialogType, apiBuildings]);

  const buildingsForEditDialog = useMemo(() => {
    if (!editDialogType) return [];
    return apiBuildings.filter((building) => {
      const matchesType = building.type === editDialogType || !building.type;
      if (!matchesType) return false;
      if (editDialogProject) {
        const buildingProjectId = building.projectId || building.project_id;
        return buildingProjectId && String(buildingProjectId) === String(editDialogProject);
      }
      return true;
    });
  }, [editDialogType, editDialogProject, apiBuildings]);

  const floorsForEditDialog = useMemo(() => {
    if (!editDialogBuildingId) return [];
    return floorCatalog.filter((floor) => floor.buildingId === editDialogBuildingId);
  }, [editDialogBuildingId, floorCatalog]);

  const runWithLoader = useCallback(
    async (task) => {
      setGlobalLoading(true);
      try {
        await task();
      } finally {
        setGlobalLoading(false);
      }
    },
    []
  );

  const selectedFlats = useMemo(() => {
    if (!hasSearched) return [];
    if (!activeSelection.building) return [];

    if (activeSelection.floor) {
      return flatStore[activeSelection.floor] || [];
    }

    const buildingFloors = activeSelection.building === "all"
      ? floorCatalog
      : floorCatalog.filter(floor => floor.buildingId === activeSelection.building);
    const allFlats = [];
    buildingFloors.forEach(floor => {
      const floorFlats = flatStore[floor.id] || [];
      allFlats.push(...floorFlats);
    });
    return allFlats;
  }, [flatStore, activeSelection.building, activeSelection.floor, hasSearched, floorCatalog]);

  const paginatedFlats = useMemo(() => {
    const start = page * rowsPerPage;
    return selectedFlats.slice(start, start + rowsPerPage);
  }, [selectedFlats, page, rowsPerPage]);

  const exportRows = useMemo(() => {
    const formatRow = (flat, floorId) => {
      const floor = floorCatalog.find((item) => (item._id || item.id) === floorId) || null;
      return {
        floorNo: floor?.floorNo || "-",
        flatNumber: flat.number || "-",
        "Type": flat.type || "-",
        Facing: flat.facing || "-",
        "PLC%": flat.plc || "0",
        "Disc%": flat.discount || `${flat.companyDiscountPercent ?? 0}%`,
        "Cost (Lacs)": flat.costLacs || "0",
        Status: flat.status || "-",
        Remark: flat.remark || "",
      };
    };

    const entries = [];
    if (hasSearched && selectedFlats.length && activeSelection.building) {
      const buildingFloors = floorCatalog.filter(floor => floor.buildingId === activeSelection.building);
      buildingFloors.forEach(floor => {
        const floorFlats = flatStore[floor.id] || [];
        if (floorFlats.length > 0) {
          entries.push([floor.id, floorFlats]);
        }
      });
    } else {
      entries.push(...Object.entries(flatStore));
    }

    return entries.flatMap(([floorId, flats]) =>
      (flats || []).map((flat) => formatRow(flat, floorId))
    );
  }, [flatStore, floorCatalog, apiBuildings, hasSearched, selectedFlats, activeSelection.building]);

  const handleExportExcel = async () => {
    try {
      const projectIdArg = draftProject !== "all" ? draftProject : undefined;
      const buildingIdArg = draftBuilding !== "all" ? draftBuilding : undefined;

      if (!buildingIdArg || !projectIdArg) {
        handleCloseExportMenu();
        return;
      }

      const params = new URLSearchParams({
        buildingId: buildingIdArg,
        projectId: projectIdArg,
        page: "1",
        limit: "100000",
      });
      const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/flats?${params.toString()}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      const body = await response.json().catch(() => ({}));
      const flats = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
          ? body.data
          : [];

      const rows = flats.map((flat, idx) => {
        const statusValue = flat.status || "available";
        const priceLacs = flat.price ? (flat.price / 100000).toFixed(2) : flat.price || flat.rate || "";
        const discountValue = flat.companyDiscountPercent || flat.discount || "0";
        const adjustedPriceLacs = computeCost(priceLacs, flat.plcPercent || flat.plc || 0, discountValue).costLacs || priceLacs;

        return {
          "floorNo": flat.floorNo || flat.floorName || "-",
          "flatNumber": flat.flatNumber || flat.number || "-",
          "Type": flat.flatType || flat.type || "-",
          Facing: flat.facing || "-",
          "PLC%": `${flat.plcPercent || flat.plc || 0}%`,
          "Disc%": `${discountValue}%`,
          "Cost (Lacs)": flat.cost ? (flat.cost / 100000).toFixed(2) : (flat.costLacs || adjustedPriceLacs),
          Status: normalizeStatus(statusValue),
          Remark: flat.remark || "",
        };
      });

      if (!rows.length) {
        handleCloseExportMenu();
        return;
      }

      exportAsExcel({ rows, sheetName: "Flats", fileNameBase: "flats" });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Export flats error", error);
    } finally {
      handleCloseExportMenu();
    }
  };

  const handleExportCSV = () => {
    const rows = exportRows;
    if (!rows.length) {
      handleCloseExportMenu();
      return;
    }
    exportAsCSV({ rows, fileNameBase: "flats" });
    handleCloseExportMenu();
  };

  const handleExportPDF = () => {
    const rows = exportRows;
    if (!rows.length) {
      handleCloseExportMenu();
      return;
    }
    const headers = Object.keys(rows[0] || {});
    exportAsPDF({
      rows,
      headers,
      title: "Flat Inventory",
      fileNameBase: "flats",
      headStyles: { fillColor: [25, 118, 210] },
    });
    handleCloseExportMenu();
  };

  const statusChipStyle = (status) => {
    switch (status) {
      case "Available":
        return { backgroundColor: "rgba(76, 175, 80, 0.12)", color: "#2e7d32" };
      case "Booked":
        return { backgroundColor: "rgba(25, 118, 210, 0.12)", color: "#1976d2" };
      case "underConstruction":
        return { backgroundColor: "rgba(255, 193, 7, 0.12)", color: "#f57c00" };
      case "Hold":
        return { backgroundColor: "rgba(244, 67, 54, 0.12)", color: "#d32f2f" };
      default:
        return { backgroundColor: "rgba(158, 158, 158, 0.12)", color: "#616161" };
    }
  };

  const normalizeStatus = (value) => {
    const s = (value || "").toLowerCase();
    switch (s) {
      case "available":
        return "Available";
      case "booked":
        return "Booked";
      case "under construction":
      case "under_construction":
      case "underconstruction":
        return "underConstruction";
      case "hold":
        return "Hold";
      default:
        return value || "";
    }
  };

  const numericLimiter = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const cleaned = value.toString().replace(/[^0-9.]/g, "");
    // prevent multiple dots
    const parts = cleaned.split(".");
    const normalized = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
    return normalized;
  };

  const parseNumberFromString = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    const num = Number(String(val).replace(/[^\d.]/g, ""));
    return Number.isFinite(num) ? num : 0;
  };

  const computeCost = (priceValue, plcValue, discountValue) => {
    // Treat price as value in lakhs and return both rupee cost and lakhs display
    const priceNum = parseNumberFromString(priceValue);
    const plcNum = parseNumberFromString(plcValue);
    const discountNum = parseNumberFromString(discountValue);

    if (!priceNum || Number.isNaN(priceNum)) {
      return { cost: 0, costLacs: "" };
    }

    const discountedBase = priceNum * (1 - discountNum / 100);
    const plcAmount = priceNum * (plcNum / 100);
    const totalLacs = discountedBase + plcAmount;
    const lacsDisplay = totalLacs > 0 ? totalLacs.toFixed(2) : "";

    return { cost: totalLacs * 100000, costLacs: lacsDisplay };
  };

  const handleSearch = async (useLoader = true) => {
    if (draftType === "all" || draftProject === "all") {
      setSearchError("Please select property type and project before searching.");
      setHasSearched(false);
      return;
    }

    try {
      setSearchError("");

      const projectIdArg = draftProject !== 'all' ? draftProject : undefined;
      const buildingIdArg = draftBuilding;


      const searchParams = {
        buildingId: buildingIdArg !== "all" ? buildingIdArg : undefined,
        floorId: draftFloor !== "all" ? draftFloor : undefined,
        projectId: projectIdArg,
      };

      const executor = useLoader ? runWithLoader : async (fn) => { await fn(); };
      await executor(async () => {
        const flatsResponse = await searchFlats(searchParams);

        const newFlatStore = {};
        // API returns { status, message, data: [...], extra: {...} }
        const flatsArray = Array.isArray(flatsResponse?.data)
          ? flatsResponse.data
          : Array.isArray(flatsResponse)
            ? flatsResponse
            : [];
        if (Array.isArray(flatsArray)) {
          flatsArray.forEach(flat => {
            const floorId = flat.floorId || "FLR-001";
            const floorRef = floorCatalog.find((f) => (f._id || f.id) === floorId) || null;
            const buildingId =
              flat.buildingId ||
              floorRef?.buildingId ||
              (draftBuilding !== "all" ? draftBuilding : "") ||
              activeSelection.building ||
              "";
            const buildingRef =
              apiBuildings.find(
                (b) =>
                  b.id === buildingId ||
                  b._id === buildingId ||
                  b.buildingId === buildingId
              ) || null;
            const projectId =
              flat.projectId ||
              buildingRef?.projectId ||
              (draftProject !== "all" ? draftProject : "") ||
              activeSelection.project ||
              "";
            if (!newFlatStore[floorId]) {
              newFlatStore[floorId] = [];
            }

            const priceLacs = flat.price ? (flat.price / 100000).toFixed(2) : "";
            const plcPercentValue = flat.plcPercent ?? 0;
            const discountValue = flat.companyDiscountPercent ?? 0;
            const computedCost = computeCost(priceLacs, plcPercentValue, discountValue);

            const mappedFlat = {
              id: flat._id,
              _id: flat._id,
              number: flat.flatNumber,
              type: getFlatTypeLabel(flat.flatType),
              typeId: getFlatTypeValue(flat.flatType),
              facing: getFacingLabel(flat.facing),
              facingId: getFacingValue(flat.facing),
              buildingName: buildingRef?.name || flat.buildingName || "",
              floorName: floorRef?.name || (Number.isFinite(Number(flat.floorNo)) ? `Floor ${flat.floorNo}` : ""),
              price: priceLacs || "",
              plc: plcPercentValue ? `${plcPercentValue}%` : '',
              companyDiscountPercent: discountValue,
              discount: `${discountValue}%`,
              costLacs: flat.cost
                ? (flat.cost / 100000).toFixed(2)
                : computedCost.costLacs,
              guidelineValue: flat.guidelineValue || '',
              inventoryType: flat.inventoryType || 'flat',
              status: normalizeStatus(flat.status) || 'Available',
              remark: flat.remark || '',
              originalFloorId: floorId,
              buildingId,
              projectId,
            };

            newFlatStore[floorId].push(mappedFlat);
          });
        }

        setFlatStore(newFlatStore);
      });

      setHasSearched(true);
      setActiveSelection({
        type: draftType,
        project: draftProject,
        building: draftBuilding,
        floor: draftFloor !== "all" ? draftFloor : "",
      });
      setPage(0);
    } catch (error) {
      setSearchError(error.message || "Failed to load flats. Please try again.");
      setHasSearched(false);
    }
  };

  const handleReset = () => {
    setDraftType("all");
    setDraftProject("all");
    setDraftBuilding("all");
    setDraftFloor("all");
    setActiveSelection({ type: "", project: "", building: "", floor: "" });
    setHasSearched(false);
    setSearchError("");
    setPage(0);
  };

  const selectedBuilding = apiBuildings.find((building) => building.id === activeSelection.building || building._id === activeSelection.building);
  const selectedFloor = floorCatalog.find((floor) => (floor._id || floor.id) === activeSelection.floor);

  const handleOpenAddDialog = () => {
    loadFlatTypes();
    loadFacingOptions();
    setNewFlat({
      number: "",
      type: "",
      facing: "",
      price: "",
      plc: "",
      companyDiscountPercent: "",
      costLacs: "",
      guidelineValue: "",
      inventoryType: "",
      status: "",
      remark: "",
    });
    const defaultFloorId = activeSelection.floor || (draftFloor !== "all" ? draftFloor : "");
    const defaultFloor = floorCatalog.find((floor) => (floor._id || floor.id) === defaultFloorId) || null;
    const defaultBuildingId = defaultFloor
      ? defaultFloor.buildingId
      : activeSelection.building || (draftBuilding !== "all" ? draftBuilding : "");
    const defaultBuilding = defaultBuildingId
      ? apiBuildings.find(
        (building) =>
          building.id === defaultBuildingId ||
          building._id === defaultBuildingId ||
          building.buildingId === defaultBuildingId,
      ) || null
      : null;
    const inferredType = defaultBuilding
      ? defaultBuilding.type || ""
      : draftType !== "all"
        ? draftType
        : "";
    const inferredProjectId = defaultBuilding
      ? defaultBuilding.projectId || ""
      : draftProject !== "all"
        ? draftProject
        : "";
    setAddDialogType(inferredType);
    setAddDialogProject(inferredProjectId);
    setAddDialogBuildingId(defaultBuilding ? defaultBuilding.id : "");
    setAddDialogFloorId(defaultFloor ? defaultFloor.id : "");
    setDialogError("");
    setAddDialogOpen(true);
  };

  const handleSaveNewFlat = async () => {
    if (!addDialogType || !addDialogProject || !addDialogBuildingId || !addDialogFloorId) {
      setDialogError("Please select property type, project, building, and floor.");
      return;
    }
    const buildingId = addDialogBuildingId;
    const floorId = addDialogFloorId;
    const { number, type, facing, price, plc, companyDiscountPercent, guidelineValue, inventoryType, status, remark } = newFlat;
    if (!number || !type || !status) {
      setDialogError("Flat number, type, and status are required.");
      return;
    }
    const flatTypeLabel = getFlatTypeLabel(type);
    const facingLabel = getFacingLabel(facing);

    // Resolve projectId from selected building or from current project selection
    const selectedBuilding =
      apiBuildings.find(
        (b) => b.id === addDialogBuildingId || b._id === addDialogBuildingId,
      ) || null;
    const projectId =
      selectedBuilding?.projectId ||
      addDialogProject ||
      (draftProject && draftProject !== "all" ? draftProject : "");

    if (!projectId) {
      setDialogError("Unable to determine project for this flat. Please reselect filters.");
      return;
    }

    const flatNumber = Number.isFinite(Number(number))
      ? Number(number)
      : parseNumberFromString(number);
    const priceNumberLacs = parseNumberFromString(price);
    const plcPercentNumber = parseNumberFromString(plc);
    const discountNumber = parseNumberFromString(companyDiscountPercent);
    const { cost: computedCost, costLacs } = computeCost(priceNumberLacs, plcPercentNumber, discountNumber);
    const priceRupees = priceNumberLacs * 100000;

    const payload = {
      projectId,
      buildingId,
      floorId,
      flatNumber,
      flatType: flatTypeLabel,
      facing: facingLabel,
      price: priceRupees,
      plcPercent: plcPercentNumber,
      companyDiscountPercent: discountNumber,
      cost: computedCost,
      guidelineValue: guidelineValue || "",
      inventoryType: inventoryType || addDialogType || "Residential",
      status: (status || "").toLowerCase() || "available",
      remark: remark || "",
    };

    try {
      setDialogError("");
      await runWithLoader(async () => {
        await createFlat(payload);
        await handleSearch(false);
      });

      setAddDialogOpen(false);
      setHasSearched(true);
      setNewFlat({
        number: "",
        type: "",
        facing: "",
        price: "",
        plc: "",
        companyDiscountPercent: "",
        costLacs,
        guidelineValue: "",
        inventoryType: "",
        status: "",
        remark: "",
      });
      setActiveSelection({
        type: addDialogType,
        project: addDialogProject,
        building: buildingId,
        floor: floorId,
      });
      setDraftType(addDialogType);
      setDraftProject(addDialogProject);
      setDraftBuilding(buildingId);
      setDraftFloor(floorId);
      setPage(0);
      setAddDialogType("");
      setAddDialogProject("");
      setAddDialogBuildingId("");
      setAddDialogFloorId("");
    } catch (error) {
      console.error("Error creating flat:", error);
      setDialogError(error.message || "Failed to create flat. Please try again.");
    }
  };

  const handleEditFlat = (flat) => {
    loadFlatTypes();
    loadFacingOptions();
    const floorId =
      flat.originalFloorId ||
      flat.floorId ||
      activeSelection.floor ||
      (draftFloor !== "all" ? draftFloor : "");
    const floorRef = floorCatalog.find((f) => (f._id || f.id) === floorId) || null;
    const buildingId =
      flat.buildingId ||
      floorRef?.buildingId ||
      activeSelection.building ||
      (draftBuilding !== "all" ? draftBuilding : "");
    const buildingRef =
      apiBuildings.find((b) => b.id === buildingId || b._id === buildingId || b.buildingId === buildingId) ||
      null;
    const projectId =
      flat.projectId ||
      buildingRef?.projectId ||
      activeSelection.project ||
      (draftProject !== "all" ? draftProject : "");
    const propertyType =
      buildingRef?.type ||
      flat.inventoryType ||
      activeSelection.type ||
      (draftType !== "all" ? draftType : "");

    setEditDialogType(propertyType || "");
    setEditDialogProject(projectId || "");
    setEditDialogBuildingId(buildingId || "");
    setEditDialogFloorId(floorId || "");
    setEditFlatState({
      ...flat,
      type: flat.typeId || getFlatTypeValue(flat.type),
      facing: flat.facingId || getFacingValue(flat.facing),
      status: normalizeStatus(flat.status),
      originalFloorId: floorId || "",
      buildingId: buildingId || "",
      projectId: projectId || "",
    });
    setDialogError("");
    setEditDialogOpen(true);
  };

  const handleUpdateFlat = async () => {
    const {
      id,
      number,
      type,
      facing,
      price,
      plc,
      companyDiscountPercent,
      costLacs,
      guidelineValue,
      inventoryType,
      status,
      remark,
      originalFloorId,
    } = editFlatState;

    if (!number || !type || !status) {
      setDialogError("Flat number, type, and status are required.");
      return;
    }

    try {
      const flatId = editFlatState._id || id;

      const flatTypeLabel = getFlatTypeLabel(type);
      const facingLabel = getFacingLabel(facing);
      const plcPercentNumber = parseNumberFromString(plc);
      const discountNumber = parseNumberFromString(companyDiscountPercent);
      const priceNumberLacs = parseNumberFromString(price);

      const { cost: computedCost, costLacs: computedCostLacs } = computeCost(
        price,
        plcPercentNumber,
        discountNumber
      );

      const guidelineValueNumber = parseNumberFromString(guidelineValue);

      const selectedEditBuilding =
        apiBuildings.find(
          (b) => b.id === editDialogBuildingId || b._id === editDialogBuildingId
        ) || null;
      const projectIdToUse =
        editDialogProject ||
        selectedEditBuilding?.projectId ||
        (activeSelection.project && activeSelection.project !== "all" ? activeSelection.project : "");

      const payload = {
        projectId: projectIdToUse,
        buildingId: editDialogBuildingId,
        floorId: editDialogFloorId,

        flatNumber: number,

        // Basic Details
        flatType: flatTypeLabel,
        facing: facingLabel,
        inventoryType: inventoryType || editDialogType,
        status: (status || "").toLowerCase() || "available",
        remark: remark || "",

        // Financials
        price: priceNumberLacs * 100000, // Converting Lacs to Absolute value (e.g. 7 -> 700000)
        plcPercent: plcPercentNumber,
        companyDiscountPercent: discountNumber,
        cost: computedCost,
        guidelineValue: guidelineValueNumber,
      };

      console.log("Sending Update Payload:", payload); // Debugging ke liye

      // 2. API Call
      setEditSaving(true);
      await runWithLoader(async () => {
        await updateFlat(flatId, payload);
      });
      setEditSaving(false);

      // 3. Local State Update (UI Refresh logic)
      const targetFloorId = editDialogFloorId;
      const sourceFloorId = originalFloorId || targetFloorId;

      const updatedFlat = {
        id,
        _id: editFlatState._id,
        number,
        type: flatTypeLabel,
        facing: facingLabel,
        price: price, // Display value (Lacs)
        plc: plc ? `${plcPercentNumber}%` : "",
        companyDiscountPercent: discountNumber,
        discount: `${discountNumber}%`,
        costLacs: computedCostLacs,
        guidelineValue,
        inventoryType,
        status: normalizeStatus(status),
        remark,
        // Update location in local object
        projectId: projectIdToUse,
        buildingId: editDialogBuildingId,
        floorId: editDialogFloorId
      };

      setFlatStore((prev) => {
        const next = { ...prev };

        // Agar floor change hua hai, purane floor se hatao
        if (sourceFloorId && sourceFloorId !== targetFloorId) {
          next[sourceFloorId] = (next[sourceFloorId] || []).filter((flat) => flat.id !== id);
        }

        // Naye/Current floor list mein add/update karo
        const targetList = next[targetFloorId] ? [...next[targetFloorId]] : [];
        const existingIndex = targetList.findIndex((flat) => flat.id === id);

        if (existingIndex >= 0) {
          targetList[existingIndex] = updatedFlat;
        } else {
          targetList.push(updatedFlat);
        }

        next[targetFloorId] = targetList;
        return next;
      });

      // Reset Dialog & Refresh View settings
      const targetType = editDialogType;
      const targetProject = projectIdToUse;
      const targetBuildingId = editDialogBuildingId;

      setEditDialogOpen(false);
      setDialogError("");
      setHasSearched(true);

      // View ko wahan switch karo jahan flat move hua hai (ya edit hua hai)
      setActiveSelection({
        type: targetType,
        project: targetProject,
        building: targetBuildingId,
        floor: targetFloorId,
      });
      setDraftType(targetType);
      setDraftProject(targetProject);
      setDraftBuilding(targetBuildingId);
      setDraftFloor(targetFloorId);
      setPage(0);

      // Cleanup State
      setEditDialogType("");
      setEditDialogProject("");
      setEditDialogBuildingId("");
      setEditDialogFloorId("");
      setEditFlatState({
        id: "",
        number: "",
        type: "",
        facing: "",
        price: "",
        plc: "",
        companyDiscountPercent: "",
        costLacs: "",
        guidelineValue: "",
        inventoryType: "",
        status: "Available",
        remark: "",
        originalFloorId: "",
      });

    } catch (error) {
      console.error('Error updating flat:', error);
      setDialogError(error.message || 'Failed to update flat. Please try again.');
    }
  };

  const handleDeleteFlat = async (flat) => {
    if (window.confirm(`Delete flat ${flat.number}? This action cannot be undone.`)) {
      try {
        // Use _id if available, fallback to id
        const flatId = flat._id || flat.id;
        await runWithLoader(async () => {
          await deleteFlat(flatId);

          // Remove from local state and refresh data automatically
          const floorId = activeSelection.floor;
          setFlatStore((prev) => {
            const next = { ...prev };
            next[floorId] = (next[floorId] || []).filter((item) => item.id !== flat.id);
            return next;
          });

          // Automatically refresh the search results
          await handleSearch(false);
        });
      } catch (error) {
        console.error('Error deleting flat:', error);
        alert(`Failed to delete flat: ${error.message}`);
      }
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Flat Inventory. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{ 
              backgroundColor: '#536DFE', 
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none' 
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: "column" }}
        open={globalLoading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          Please wait...
        </Typography>
      </Backdrop>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ProjectIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4" fontWeight="bold" color="primary">Flat Inventory</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {canWrite && (
            <>
              <Button variant="contained" startIcon={<FileUploadIcon />} onClick={handleImportClick}>Import</Button>
              <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleOpenExportMenu}>Export</Button>
              <Menu
                anchorEl={exportMenuAnchorEl}
                open={Boolean(exportMenuAnchorEl)}
                onClose={handleCloseExportMenu}
              >
                <MenuItem onClick={handleExportExcel}>Export as Excel</MenuItem>
                <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
                <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
              </Menu>
            </>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => handleSearch()} disabled={!hasSearched}>Refresh</Button>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => history.push('/app/project/apartment/flat/add', {
              project: activeSelection.project,
              building: activeSelection.building,
              floor: activeSelection.floor,
              type: activeSelection.type
            })} disabled={!canAddFlat}>Add New Flat</Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel shrink>Property Type</InputLabel>
              <Select
                value={draftType}
                onChange={(e) => {
                  const value = e.target.value;
                  setDraftType(value);
                  setDraftProject("all");
                  setDraftBuilding("all");
                  setDraftFloor("all");
                }}
                label="Property Type"
              >
                <MenuItem value="all">Select Type</MenuItem>
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel shrink>Project</InputLabel>
              <Select
                value={draftProject}
                onChange={(e) => {
                  setDraftProject(e.target.value);
                  setDraftBuilding("all");
                  setDraftFloor("all");
                }}
                label="Project"
              >
                {projectOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel shrink>Building</InputLabel>
              <Select
                value={draftBuilding}
                onChange={(e) => {
                  if (!draftProject || draftProject === "all") {
                    setSearchError("Please select project first.");
                    setDraftBuilding("all");
                    return;
                  }
                  setDraftBuilding(e.target.value);
                  setDraftFloor("all");
                }}
                label="Building"
              >
                <MenuItem value="all">Select Building</MenuItem>
                {buildingOptions.map((building) => (
                  <MenuItem key={building.id} value={building.id}>{building.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel shrink>Floor</InputLabel>
              <Select
                value={draftFloor}
                onChange={(e) => setDraftFloor(e.target.value)}
                label="Floor"
              >
                 <MenuItem value="all">All Floors</MenuItem>
                  {floorOptions.map((floor) => {
                    const label = floor.name || (Number.isFinite(Number(floor.floorNo)) ? `Floor ${floor.floorNo}` : floor.floorNo) || floor.id;
                    const displayLabel = draftBuilding === "all" ? `${label} (${floor.buildingName || 'Unknown Building'})` : label;
                    return (
                      <MenuItem key={floor._id || floor.id} value={floor._id || floor.id}>
                        {displayLabel}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>Search</Button>
              <Button variant="outlined" onClick={() => {
                setDraftType("all");
                setDraftProject("all");
                setDraftBuilding("all");
                setDraftFloor("all");
                setHasSearched(false);
              }}>Reset</Button>
            </Box>
          </Grid>
          {searchError && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">{searchError}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* {selectedBuilding && selectedFloor && hasSearched && (
        <Card sx={{ mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: 2 }}>
          <CardContent sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Building
              </Typography>
              <Typography variant="body2">{selectedBuilding.name}</Typography>
              <Chip
                label={selectedBuilding.project}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mt: 1, fontWeight: 600 }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Floor
              </Typography>
              <Typography variant="body2">{selectedFloor.name}</Typography>
            </Box>
            {selectedFlats.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Summary
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedFlats.length} flat{selectedFlats.length > 1 ? "s" : ""} listed
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )} */}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            {hasSearched && draftProject !== "all" && (
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                   {activeSelection.building === "all" && <TableCell sx={{ fontWeight: 700 }}>Building</TableCell>}
                  <TableCell sx={{ fontWeight: 700 }}>Floor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Flat Number</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Flat Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Facing</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Price (Lacs)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>PLC %</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Discount %</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cost (Lacs)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Circle Rate</TableCell>
                  {/* <TableCell sx={{ fontWeight: 700 }}>Inventory Type</TableCell> */}
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {paginatedFlats.map((flat) => (
                <TableRow hover key={flat.id}>
                   {activeSelection.building === "all" && <TableCell>{flat.buildingName}</TableCell>}
                  <TableCell>{flat.floorName}</TableCell>
                  <TableCell><Typography fontWeight="600">{flat.number}</Typography></TableCell>
                  <TableCell>{flat.type}</TableCell>
                  <TableCell>{flat.facing}</TableCell>
                  <TableCell>{flat.costLacs ? `${flat.costLacs} L` : ""}</TableCell>
                  <TableCell>{flat.plc}</TableCell>
                  <TableCell>{flat.discount || `${flat.companyDiscountPercent ?? 0}%`}</TableCell>
                  <TableCell>{flat.costLacs}</TableCell>
                  <TableCell>{flat.guidelineValue}</TableCell>
                  {/* <TableCell><Chip label={flat.inventoryType} size="small" variant="outlined" sx={{ fontWeight: 600 }} /></TableCell> */}
                  <TableCell><Chip label={flat.status} size="small" sx={statusChipStyle(flat.status)} /></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{flat.remark || "—"}</Typography></TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {canWrite ? (
                        <>
                          <IconButton size="small" color="primary" onClick={() => history.push(`/app/project/apartment/flat/edit/${flat.id}`, { flat })}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteFlat(flat)}><DeleteIcon fontSize="small" /></IconButton>
                        </>
                      ) : (
                        <></>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedFlats.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasSearched ? "No flats found for the selected floor." : "Select project, building, and floor, then click Search to view flats."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {hasSearched && draftProject !== "all" && (
          <TablePagination
            // rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={selectedFlats.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        )}
      </Paper>      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'white', mb: 2 }}>
          <ProjectIcon />
          <Typography variant="h6">Add New Flat</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">Hierarchy Selection</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel shrink>Property Type</InputLabel>
                <Select
                  value={addDialogType}
                  onChange={(e) => {
                    setAddDialogType(e.target.value);
                    setAddDialogProject("");
                    setAddDialogBuildingId("");
                    setAddDialogFloorId("");
                  }}
                  label="Property Type"
                >
                  {propertyTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={!addDialogType}>
                <InputLabel shrink>Project</InputLabel>
                <Select
                  value={addDialogProject}
                  onChange={(e) => {
                    setAddDialogProject(e.target.value);
                    setAddDialogBuildingId("");
                    setAddDialogFloorId("");
                  }}
                  label="Project"
                >
                  {projectsForAddDialog.map((project) => (
                    <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={!addDialogProject}>
                <InputLabel shrink>Building</InputLabel>
                <Select
                  value={addDialogBuildingId}
                  onChange={(e) => {
                    setAddDialogBuildingId(e.target.value);
                    setAddDialogFloorId("");
                  }}
                  label="Building"
                >
                  {buildingsForAddDialog.map((building) => (
                    <MenuItem key={building.id} value={building.id}>{building.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={!addDialogBuildingId}>
                <InputLabel shrink>Floor</InputLabel>
                <Select
                  value={addDialogFloorId}
                  onChange={(e) => setAddDialogFloorId(e.target.value)}
                  label="Floor"
                >
                  {floorsForAddDialog.map((floor) => (
                    <MenuItem key={floor._id || floor.id} value={floor._id || floor.id}>Floor {floor.floorNo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary" sx={{ mt: 4 }}>Flat Details</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Flat Number"
                value={newFlat.number}
                onChange={(e) => setNewFlat((prev) => ({ ...prev, number: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Flat Type</InputLabel>
                <Select
                  value={newFlat.type}
                  onChange={(e) => setNewFlat((prev) => ({ ...prev, type: e.target.value }))}
                  label="Flat Type"
                >
                  {flatTypeOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Facing</InputLabel>
                <Select
                  value={newFlat.facing}
                  onChange={(e) => setNewFlat((prev) => ({ ...prev, facing: e.target.value }))}
                  label="Facing"
                >
                  {facingOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary" sx={{ mt: 4 }}>Pricing & Status</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Price (Lacs)"
                type="number"
                value={newFlat.price}
                onChange={(e) => {
                  const val = numericLimiter(e.target.value);
                  setNewFlat((prev) => ({
                    ...prev,
                    price: val,
                    costLacs: computeCost(val, prev.plc, prev.companyDiscountPercent).costLacs,
                  }));
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Discount (%)"
                type="number"
                value={newFlat.companyDiscountPercent}
                onChange={(e) => {
                  const val = numericLimiter(e.target.value);
                  setNewFlat((prev) => ({
                    ...prev,
                    companyDiscountPercent: val,
                    costLacs: computeCost(prev.price, prev.plc, val).costLacs,
                  }));
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="PLC %"
                type="number"
                value={newFlat.plc}
                onChange={(e) => {
                  const val = numericLimiter(e.target.value);
                  setNewFlat((prev) => ({
                    ...prev,
                    plc: val,
                    costLacs: computeCost(prev.price, val, prev.companyDiscountPercent).costLacs,
                  }));
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Cost (Lacs)" value={newFlat.costLacs} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Circle Rate"
                type="number"
                value={newFlat.guidelineValue}
                onChange={(e) => setNewFlat((prev) => ({ ...prev, guidelineValue: numericLimiter(e.target.value) }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  value={newFlat.status}
                  onChange={(e) => setNewFlat((prev) => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="Available">Available</MenuItem>
                  <MenuItem value="Booked">Booked</MenuItem>
                  <MenuItem value="underConstruction">Under Construction</MenuItem>
                  <MenuItem value="Hold">Hold</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                value={newFlat.remark}
                onChange={(e) => setNewFlat((prev) => ({ ...prev, remark: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
          {dialogError && addDialogOpen && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>{dialogError}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setAddDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleSaveNewFlat}>Save Flat</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'white', mb: 2 }}>
          <EditIcon />
          <Typography variant="h6">Edit Flat</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">Hierarchy Selection</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled>
                <InputLabel shrink>Property Type</InputLabel>
                <Select value={editDialogType} label="Property Type">
                  {propertyTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled>
                <InputLabel shrink>Project</InputLabel>
                <Select value={editDialogProject} label="Project">
                  {projectsForAddDialog.map((project) => (
                    <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled>
                <InputLabel shrink>Building</InputLabel>
                <Select value={editFlatState.buildingId} label="Building">
                  {buildingsForAddDialog.map((building) => (
                    <MenuItem key={building.id} value={building.id}>{building.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled>
                <InputLabel shrink>Floor</InputLabel>
                <Select value={editFlatState.floorId} label="Floor">
                  {floorsForAddDialog.map((floor) => (
                    <MenuItem key={floor._id || floor.id} value={floor._id || floor.id}>Floor {floor.floorNo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary" sx={{ mt: 4 }}>Flat Details</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Flat Number"
                value={editFlatState.number}
                onChange={(e) => setEditFlatState((prev) => ({ ...prev, number: e.target.value }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Flat Type</InputLabel>
                <Select
                  value={editFlatState.type}
                  onChange={(e) => setEditFlatState((prev) => ({ ...prev, type: e.target.value }))}
                  label="Flat Type"
                >
                  {flatTypeOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Facing</InputLabel>
                <Select
                  value={editFlatState.facing}
                  onChange={(e) => setEditFlatState((prev) => ({ ...prev, facing: e.target.value }))}
                  label="Facing"
                >
                  {facingOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary" sx={{ mt: 4 }}>Pricing & Status</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Price (Lacs)"
                type="number"
                value={editFlatState.price}
                onChange={(e) => {
                  const val = numericLimiter(e.target.value);
                  setEditFlatState((prev) => ({
                    ...prev,
                    price: val,
                    costLacs: computeCost(val, prev.plc, prev.companyDiscountPercent).costLacs,
                  }));
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Discount (%)"
                type="number"
                value={editFlatState.companyDiscountPercent}
                onChange={(e) => {
                  const val = numericLimiter(e.target.value);
                  setEditFlatState((prev) => ({
                    ...prev,
                    companyDiscountPercent: val,
                    costLacs: computeCost(prev.price, prev.plc, val).costLacs,
                  }));
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="PLC %"
                type="number"
                value={editFlatState.plc}
                onChange={(e) => {
                  const val = numericLimiter(e.target.value);
                  setEditFlatState((prev) => ({
                    ...prev,
                    plc: val,
                    costLacs: computeCost(prev.price, val, prev.companyDiscountPercent).costLacs,
                  }));
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Cost (Lacs)" value={editFlatState.costLacs} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Circle Rate"
                type="number"
                value={editFlatState.guidelineValue}
                onChange={(e) => setEditFlatState((prev) => ({ ...prev, guidelineValue: numericLimiter(e.target.value) }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  value={editFlatState.status}
                  onChange={(e) => setEditFlatState((prev) => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="Available">Available</MenuItem>
                  <MenuItem value="Booked">Booked</MenuItem>
                  <MenuItem value="underConstruction">Under Construction</MenuItem>
                  <MenuItem value="Hold">Hold</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                value={editFlatState.remark}
                onChange={(e) => setEditFlatState((prev) => ({ ...prev, remark: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
          {dialogError && editDialogOpen && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>{dialogError}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => {
              setDialogError("");
              setEditDialogType("");
              setEditDialogProject("");
              setEditDialogBuildingId("");
              setEditDialogFloorId("");
              setEditFlatState({
                id: "",
                number: "",
                type: "",
                facing: "",
                price: "",
                plc: "",
                costLacs: "",
                guidelineValue: "",
                inventoryType: "",
                status: "",
                remark: "",
                originalFloorId: "",
              });
              setEditDialogOpen(false);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpdateFlat} disabled={editSaving}>
            {editSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Import Flats</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Upload an Excel file (.xlsx or .xls) with columns such as <code>floorId</code>,{" "}
              <code>buildingId</code>, <code>number</code>, <code>type</code>, <code>status</code>,{" "}
              <code>price</code>, and <code>remark</code>. Rows without a floor identifier or flat number
              will be ignored.
            </DialogContentText>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="text"
                startIcon={<FileDownloadIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleDownloadSample}
              >
                Download Sample
              </Button>
              <Button
                variant="text"
                startIcon={<FileDownloadIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
            </Stack>
            {importError && (
              <Alert severity="error" onClose={() => setImportError("")}>
                {importError}
              </Alert>
            )}
            {importSuccess && (
              <Alert severity="success" onClose={() => setImportSuccess("")}>
                {importSuccess}
              </Alert>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                sx={{ textTransform: "none" }}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </Button>
              {importFileName && (
                <Typography variant="caption" color="text.secondary">
                  {importFileName}
                </Typography>
              )}
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleChooseImportFile}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePerformImport}
            disabled={!selectedImportFile}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlatList;
